import { rd } from '@passionware/monads';
import type { Resource } from '__types__/v1/models';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import { Badge } from 'v5/design-system/Badge';
import type { AssignResourceDialogServices } from 'v5/features/productions/resourcing/assign-resource-dialog/assignResourceCandidate';
import {
  ProductionResourcePicker,
  useProductionResourcePicker,
  withAvailableChip
} from 'v5/features/productions/resourcing/assign-resource-dialog/ProductionResourcePicker';
import { InfoCallout } from 'v5/patterns/feedback/InfoCallout';
import { ResourcePicker } from 'v5/patterns/resource-picker/ResourcePicker';
import { ResourcePickerDialog } from 'v5/patterns/resource-picker/ResourcePickerDialog';
import type { ResourcePickerSource } from 'v5/patterns/resource-picker/ResourcePickerResults';
import { usePickedResources } from 'v5/patterns/resource-picker/usePickedResources';

/** A part of the slot's saved search the search can't express (see `resourceSearchOfV1`). */
export interface SlotSearchNotApplied {
  field: string;
  operator: string;
  value: unknown;
}

/** What can be done with the picked; each one's booking in the production's dates decides how. */
interface SlotAssignActions {
  /**
   * One picked: the booking editor (on the booking, or a new one in its status). Several: each
   * booked as that editor would save them, resolving once all are saved.
   */
  assign: () => void | Promise<void>;
  /** The booking editor on a new booking in no status, for one picked. */
  assignAndEdit: () => void;
  /** Resolves once every booking is saved, confirmed. */
  assignAndConfirm: () => Promise<void>;
  /** A booking status to assign every picked with; resolves once all are saved. */
  statuses: {
    key: string;
    label: ReactNode;
    icon: ReactNode;
    assign: () => Promise<void>;
  }[];
}

/** The host's assign actions, as hooks: one adapter per host, a double in stories. */
export interface SlotAssignController {
  useActionsFor: (resources: Resource[]) => SlotAssignActions;
}

export interface SlotSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  services: AssignResourceDialogServices;
  productionId: number;
  slotName: string;
  /** The slot's saved search as the search starts, and what of it could not be applied. */
  preset: { query: ResourceSearchQuery; notApplied: SlotSearchNotApplied[] };
  /** The production has dates: the search starts with "Available" in them on. */
  startsAvailable: boolean;
  source: ResourcePickerSource;
  details: (resource: Resource, tab: 'details' | 'pinboard') => ReactNode;
  controller: SlotAssignController;
}

/**
 * Searching the whole database for a production's slot: the slot's saved search as the start,
 * the production's workspace, and the slot's ways to assign the picked.
 */
export function SlotSearchDialog({
  open,
  onOpenChange,
  ...props
}: SlotSearchDialogProps) {
  return (
    <ResourcePickerDialog open={open} onOpenChange={onOpenChange}>
      <SlotSearchBody {...props} onOpenChange={onOpenChange} />
    </ResourcePickerDialog>
  );
}

const SYMBOLS: Record<string, string> = {
  eq: '=',
  ne: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤'
};

function SlotSearchBody({
  onOpenChange,
  services,
  productionId,
  slotName,
  preset,
  startsAvailable,
  source,
  details,
  controller
}: Omit<SlotSearchDialogProps, 'open'>) {
  const { t } = useTranslation('productions');
  const [query, setQuery] = useState(() =>
    withAvailableChip(
      preset.query,
      startsAvailable ? { operator: 'equal', value: true } : null
    )
  );
  const picker = useProductionResourcePicker({
    services,
    productionId,
    query,
    onQueryChange: setQuery
  });
  const picked = usePickedResources();
  const [opened, setOpened] = useState<{
    resource: Resource;
    tab: 'details' | 'pinboard';
  } | null>(null);
  const pickedResources = rd.tryGet(
    services.resourceService.useItemsById([...picked.ids])
  );
  const actions = controller.useActionsFor(pickedResources ?? []);
  // the actions read who is picked: off until all of them are loaded
  const ready = pickedResources?.length === picked.ids.size;
  const leavingFor = (openEditor: () => void) => () => {
    onOpenChange(false);
    openEditor();
  };
  const fieldLabel = (field: string) => {
    switch (field) {
      case 'availability':
        return t('SlotResourcePicker.availability');
      case 'order_by':
        return t('SlotResourcePicker.sort');
      default:
        return picker.resultFields.labelOf(field);
    }
  };
  const partText = ({ operator, value }: SlotSearchNotApplied) =>
    [
      SYMBOLS[operator] ??
        t(`SlotResourcePicker.operators.${operator}`, {
          defaultValue: operator
        }),
      Array.isArray(value) ? value.join(', ') : String(value)
    ].join(' ');
  const notAppliedFields = [
    ...new Set(preset.notApplied.map(part => part.field))
  ];

  return (
    <>
      <ResourcePickerDialog.Header
        title={t('resourcingSlotContextMenuAddResource')}
        scope={picker.scope}
        services={services}
      >
        <Badge variant="gray" tone="subtle" size="sm">
          {t('resourcingAssignDialogSlot', { name: slotName })}
        </Badge>
      </ResourcePickerDialog.Header>
      <ResourcePickerDialog.Body>
        <ResourcePickerDialog.Main>
          <ProductionResourcePicker.Search picker={picker}>
            {notAppliedFields.length > 0 && (
              <InfoCallout tone="attention">
                <div>
                  {t('SlotResourcePicker.notApplied')}
                  <ul>
                    {notAppliedFields.map(field => (
                      <li key={field}>
                        {fieldLabel(field)}:{' '}
                        {preset.notApplied
                          .filter(part => part.field === field)
                          .map(partText)
                          .join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              </InfoCallout>
            )}
          </ProductionResourcePicker.Search>
          <ProductionResourcePicker.Results
            picker={picker}
            source={source}
            onOpen={(resource, tab) => setOpened({ resource, tab })}
            openId={opened?.resource.id ?? null}
            selection={{
              ids: picked.ids,
              onChange: picked.onChange,
              label: t('resourcingAssignDialogPick')
            }}
          />
        </ResourcePickerDialog.Main>
        <ResourcePicker.Inspector onClose={() => setOpened(null)}>
          {opened && (
            <ProductionResourcePicker.Details
              key={`${opened.resource.id}:${opened.tab}`}
              picker={picker}
              resource={opened.resource}
            >
              {details(opened.resource, opened.tab)}
            </ProductionResourcePicker.Details>
          )}
        </ResourcePicker.Inspector>
      </ResourcePickerDialog.Body>
      <ResourcePickerDialog.Footer>
        <ResourcePicker.PickedFooter
          count={picked.ids.size}
          onClear={picked.clear}
        >
          <ResourcePickerDialog.Cancel />
          <ResourcePicker.Actions
            count={ready ? picked.ids.size : 0}
            moreLabel={t('SlotResourcePicker.moreWays')}
            failure={t('SlotResourcePicker.assignFailed')}
            actions={[
              {
                key: 'assign',
                label: t('SlotResourcePicker.assign'),
                isEnabledFor: () => true,
                onSelect:
                  picked.ids.size === 1
                    ? leavingFor(actions.assign)
                    : actions.assign
              },
              {
                key: 'assignAndConfirm',
                label: t('SlotResourcePicker.assignAndConfirm'),
                isEnabledFor: () => true,
                onSelect: actions.assignAndConfirm
              },
              {
                key: 'assignWithStatus',
                label: t('SlotResourcePicker.assignWithStatus'),
                isEnabledFor: () => actions.statuses.length > 0,
                items: actions.statuses.map(status => ({
                  key: status.key,
                  label: status.label,
                  icon: status.icon,
                  isEnabledFor: () => true,
                  onSelect: status.assign
                }))
              },
              {
                key: 'assignAndEdit',
                label: t('SlotResourcePicker.assignAndEdit'),
                // one editor opens for one person
                isEnabledFor: count => count === 1,
                unavailableHint: t('SlotResourcePicker.assignAndEditOneOnly'),
                onSelect: leavingFor(actions.assignAndEdit)
              }
            ]}
          />
        </ResourcePicker.PickedFooter>
      </ResourcePickerDialog.Footer>
    </>
  );
}
